import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { afterSignIn, navigate, useQuery } from '../router';
import { useSession } from '../session';

/** Sign in, and the page the link in the e-mail opens.
 *
 *  The link is read first and spent second, on a button. A corporate mail
 *  scanner follows every URL in a message; if following one were enough to
 *  sign in, the scanner would burn the link before the person clicked it. */
export function SignIn() {
  const { t, setUser } = useSession();
  const query = useQuery();
  const token = query.get('token');
  const next = query.get('next');

  if (token) return <Redeem token={token} />;

  return <Ask />;

  function Ask() {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);

    const [failed, setFailed] = useState<string | null>(null);

    const submit = async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setFailed(null);
      try {
        await api.requestLink(email, next);
        setSent(true);
      } catch (err) {
        // "Check your e-mail" would be a lie if nothing was sent.
        setFailed(err instanceof ApiError ? err.message : t.linkTrouble);
      } finally {
        setBusy(false);
      }
    };

    return (
      <div className="signin">
        <img src="/logo.svg" alt="Omibee" />
        <h1>{t.signInTitle}</h1>
        <p>{t.signInLead}</p>
        {sent ? (
          <p className="notice" role="status">{t.linkSent}</p>
        ) : (
          <form onSubmit={submit}>
            {failed && <p className="error" role="alert">{failed}</p>}
            <div style={{ width: '100%' }}>
              <label htmlFor="email">{t.email}</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button className="primary" type="submit" disabled={busy || !email}>
              {t.send} &gt;
            </button>
          </form>
        )}
      </div>
    );
  }

  function Redeem({ token }: { token: string }) {
    const [name, setName] = useState<string | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
      let live = true;
      api.inspectLink(token)
        .then((result) => {
          if (!live) return;
          if (result.valid) setName(result.name ?? '');
          else setProblem(result.reason ?? t.linkTrouble);
        })
        .catch(() => { if (live) setProblem(t.linkTrouble); });
      return () => { live = false; };
    }, [token]);

    const enter = async () => {
      setBusy(true);
      try {
        const user = await api.redeemLink(token);
        setUser(user);
        navigate(afterSignIn(next), true);
      } catch (err) {
        setProblem(err instanceof ApiError ? err.message : t.linkTrouble);
        setBusy(false);
      }
    };

    return (
      <div className="signin">
        <img src="/logo.svg" alt="Omibee" />
        <h1>{t.signInTitle}</h1>
        {problem ? (
          <>
            <p className="error" role="alert">{problem}</p>
            <button className="primary" onClick={() => navigate(next ? `/signin?next=${encodeURIComponent(next)}` : '/signin', true)}>
              {t.linkTrouble} &gt;
            </button>
          </>
        ) : name === null ? (
          <p className="skeleton">{t.loading}…</p>
        ) : (
          <>
            <p>{t.signInLead}</p>
            <button className="primary" onClick={enter} disabled={busy} autoFocus>
              {busy ? `${t.signingIn}…` : `${t.continueAs(name)} >`}
            </button>
          </>
        )}
      </div>
    );
  }
}
